// @ts-nocheck
import { useEffect, useState } from 'react';
import {
  createMachine,
  state,
  state as final,
  state as initial,
  transition,
  reduce,
  guard,
  invoke,
  immediate,
} from 'robot3';
import { createUseMachine } from 'robot-hooks';
import { goBackReducer, saveCart } from './functions';
import { ACTION_TYPES, STATE } from './constants.js';
import {
  hasCustomer,
  hasNoCustomer,
  hasCompletePet,
  hasPetName,
  hasPetWeight,
  hasPetActivity,
  hasPetGender,
  hasPetStatus,
  allPetsHaveProductInCart,
  notAllPetsHaveProductInCart,
  submitCartFailed,
  isCompletePet,
  allPetsComplete,
  isAddPup,
  hasInitialPetWeight,
  hasInitialPetActivity,
  isInitialFlow,
  isEditPet,
  hasPet,
  isDeletePet,
  initialPetComplete,
  notAddPup,
  hasMealPrep,
  showCustomerInfoStep,
  skipToRecommendedPlan,
  skipUserInfoOnBack,
  notGoBack,
} from './guard';

export const useMachine = createUseMachine(useEffect, useState);

const createInitialContext = ({
  customer = {},
  pet = {},
  pet_id = '',
  recipes = { fresh: null },
  extras = [],
  planType = null,
  stateKey = STATE.initial,
  nextKey = null,
  prevState = {},
  flags = {}, // This may come in handy moving forward with V3
} = {}) => ({
  customer,
  pet,
  pet_id,
  recipes,
  extras,
  planType,
  stateKey,
  nextKey,
  prevState,
  flags,
});

export const onboardingMachine = createMachine(
  STATE.initial,
  {
    [STATE.initial]: initial(
      /**
       ** Transitions to Pup Name Page by default if
       ** - isAddPup: true
       ** - allPetsHaveProductInCart: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.pupName,
        guard(isAddPup),
        guard(allPetsHaveProductInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          emailStepLast: payload.emailStepLast,
          flags: { ...ctx.flags, ...payload.flags },
          prevState: { ...ctx },
          stateKey: STATE.pupName,
          nextKey: STATE.pupGender,
          customer: payload.customer,
          pets: payload.pets,
          pet: null,
          pet_id: '',
          isAddPup: true,
          isEditPet: false,
        })),
      ),
      /**
       ** Transitions to Pup Name Page for edit pet flow if
       ** - isEditPet: true
       ** - initialPetComplete: true
       ** - hasCustomer: true
       ** - notAddPup: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.pupName,
        guard(isEditPet),
        guard(initialPetComplete),
        guard(hasCustomer),
        guard(notAddPup),
        reduce((ctx, { payload }) => ({
          ...ctx,
          emailStepLast: payload.emailStepLast,
          flags: { ...ctx.flags, ...payload.flags },
          prevState: { ...ctx },
          stateKey: STATE.pupName,
          nextKey: STATE.pupGender,
          customer: payload.customer,
          isEditPet: true,
          isAddPup: false,
          pets: payload.pets,
          pet:
            payload.pets.find(p => p.id === payload.pet_id) ?? payload.pets[0],
          pet_id: payload.pet_id,
        })),
      ),
      /**
       ** Transitions to Delete Pet state if
       ** - hasCustomer: true
       ** - hasPet: true
       ** - isDeletePet: true
       ** - notAddPup: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.deletePet,
        guard(hasCustomer),
        guard(hasPet),
        guard(isDeletePet),
        guard(notAddPup),
        reduce((ctx, { payload }) => ({
          ...ctx,
          flags: { ...ctx.flags, ...payload.flags },
          prevState: { ...ctx },
          stateKey: STATE.deletePet,
          customer: payload.customer,
          pet_id: payload.pet_id,
          pet: payload.pets.find(p => p.id === payload.pet_id),
          deletePetFromCart: payload.deletePetFromCart,
        })),
      ),
      /**
       ** Transitions to User Info Page if
       ** - hasNoCostumer: true
       ** - hasCompletePet: true
       ** - notAllPetsHaveProductInCart: true
       ** - notAddPup: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.userInfo,
        guard(hasNoCustomer),
        guard(hasCompletePet),
        guard(notAllPetsHaveProductInCart),
        guard(notAddPup),
        guard(showCustomerInfoStep),
        reduce((ctx, { payload }) => {
          const pet = payload.pets[0];
          return {
            ...ctx,
            flags: { ...ctx.flags, ...payload.flags },
            prevState: { ...ctx },
            stateKey: STATE.userInfo,
            nextKey: STATE.recommendedPlan,
            customer: payload.customer,
            pet: pet,
            pet_id: pet?.id ?? '',
            isAddPup: false,
            isEditPet: false,
          };
        }),
      ),
      /**
       ** Transitions to Pup Info Page if page by default if
       ** - hasPet: true
       ** - notAddPup: true
       ** Pup Info then takes over and passes the pet to the correct step via immediate transitions
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.pupName,
        guard(hasPet),
        guard(notAddPup),
        reduce((ctx, { payload }) => {
          let pet = null;
          if (payload.pets?.length) {
            pet =
              payload.pets.find(pet => !isCompletePet(pet)) ?? payload.pets[0];
          }
          const isEditPet = !!payload.isEditPet || !!ctx.isEditPet;

          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.pupName,
            nextKey: STATE.pupGender,
            customer: payload.customer,
            pet: pet || payload.pet,
            pet_id: payload.pet_id,
            flags: { ...ctx.flags, ...(payload.flags || {}) },
            isEditPet: isEditPet,
            isAddPup: false,
          };
        }),
      ),
      /**
       ** Transitions to Intro Page
       ** Only show this when you first enter onboarding if
       ** - isInitialFlow: true
       ** - notAddPup: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.intro,
        guard(isInitialFlow),
        guard(notAddPup),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            flags: { ...ctx.flags, ...payload.flags },
            prevState: { ...ctx },
            stateKey: STATE.intro,
            nextKey: STATE.pupName,
            isAddPup: false,
            isEditPet: false,
          };
        }),
      ),
    ),
    [STATE.intro]: state(
      /**
       ** Route: Continue/Next
       *
       ** Transitions to PupName Page
       */
      transition(
        ACTION_TYPES.toPupName,
        STATE.pupName,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.pupName,
            nextKey: STATE.pupGender,
            pet: payload?.pet,
            pet_id: payload?.pet?.id,
            flags: { ...ctx.flags, ...payload.flags },
          };
        }),
      ),
    ),
    [STATE.pupName]: state(
      /**
       ** Immediately transition to PupGender if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasPetName: true
       */
      immediate(
        STATE.pupGender,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasPetName),
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.pupGender,
            nextKey: STATE.pupStatus,
            pet: ctx?.pet,
            pet_id: ctx?.pet?.id,
            initialFlow: true,
            flags: { ...ctx.flags },
          };
        }),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to Intro Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.intro,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.intro,
          nextKey: STATE.pupName,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to PupGender Page
       */
      transition(
        ACTION_TYPES.toPupGender,
        STATE.pupGender,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.pupGender,
            nextKey: STATE.pupStatus,
            pet: payload.pet,
            pet_id: payload.pet.id,
            flags: { ...ctx.flags, ...payload.flags },
          };
        }),
      ),
    ),
    [STATE.pupGender]: state(
      /**
       ** Immediately transition to PupStatus if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasPetGender: true
       */
      immediate(
        STATE.pupStatus,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasPetGender),
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.pupStatus,
            nextKey: STATE.weight,
            pet: ctx?.pet,
            pet_id: ctx?.pet?.id,
            initialFlow: true,
            flags: { ...ctx.flags },
          };
        }),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to PupName Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupName,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.pupName,
          nextKey: STATE.pupGender,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to PupStatus Page
       */
      transition(
        ACTION_TYPES.toPupStatus,
        STATE.pupStatus,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.pupStatus,
            nextKey: STATE.weight,
            pet: payload.pet,
            pet_id: payload.pet.id,
            flags: { ...ctx.flags, ...payload.flags },
          };
        }),
      ),
    ),
    [STATE.pupStatus]: state(
      /**
       ** Immediately transition to Weight Page if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasPetStatus: true
       */
      immediate(
        STATE.weight,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasPetStatus),
        reduce(ctx => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.weight,
            nextKey: STATE.activity,
            pet: ctx?.pet,
            pet_id: ctx?.pet?.id,
            initialFlow: true,
            flags: { ...ctx.flags },
          };
        }),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to PupGender Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupGender,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.pupGender,
          nextKey: STATE.pupStatus,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Weight Page
       */
      transition(
        ACTION_TYPES.toWeight,
        STATE.weight,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.weight,
            nextKey: STATE.activity,
            pet: payload.pet,
            pet_id: payload.pet.id,
            flags: { ...ctx.flags, ...payload.flags },
          };
        }),
      ),
    ),
    [STATE.weight]: state(
      /**
       ** Immediately transition to Activity Page if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasInitialPetWeight: true
       */
      immediate(
        STATE.activity,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasInitialPetWeight),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.activity,
          nextKey: STATE.allergies,
          pet: ctx?.pet,
          pet_id: ctx?.pet?.id,
          initialFlow: true,
          flags: { ...ctx.flags },
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to PupStatus Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupStatus,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.pupStatus,
          nextKey: STATE.weight,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Activity Selection Page if
       ** - hasPetWeight: true
       */
      transition(
        ACTION_TYPES.toActivity,
        STATE.activity,
        guard(hasPetWeight),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.activity,
          nextKey: STATE.allergies,
          pet: payload.pet,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.activity]: state(
      /**
       ** Immediately transition to Allergies Page if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasInitialPetActivity: true
       */
      immediate(
        STATE.allergies,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasInitialPetActivity),
        reduce(ctx => {
          const nextKey = ctx.flags?.skipCustomerInfo
            ? STATE.toRecommendedPlan
            : STATE.toUserInfo;

          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.allergies,
            nextKey: nextKey,
            pet: ctx?.pet,
            pet_id: ctx?.pet?.id,
            initialFlow: true,
            flags: { ...ctx.flags },
          };
        }),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to Weight Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.weight,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.weight,
          nextKey: STATE.activity,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Allergies Page if
       ** - hasPetActivity: true
       */
      transition(
        ACTION_TYPES.toAllergies,
        STATE.allergies,
        guard(hasPetActivity),
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.allergies,
            nextKey: STATE.recommendedPlan,
            isEditPet: ctx.isEditPet,
            pet: payload.pet,
            flags: { ...ctx.flags, ...payload.flags },
          };
        }),
      ),
    ),
    [STATE.allergies]: state(
      immediate(
        STATE.recommendedPlan,
        guard(isEditPet),
        guard(notGoBack),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.allergies,
          nextKey: STATE.toRecommendedPlan,
          pet: ctx?.pet,
          pet_id: ctx?.pet?.id,
          initialFlow: true,
          flags: { ...ctx.flags },
        })),
      ),
      immediate(
        STATE.recommendedPlan,
        guard(notAddPup),
        guard(hasMealPrep),
        guard(hasCustomer), //* (dependent on skipCustomerInfo flag)
        guard(skipToRecommendedPlan),
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.allergies,
          nextKey: STATE.toRecommendedPlan,
          pet: ctx?.pet,
          pet_id: ctx?.pet?.id,
          initialFlow: true,
          flags: { ...ctx.flags },
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to Activity Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.activity,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.activity,
          nextKey: STATE.allergies,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to UserInfo Page if
       ** - hasNoCustomer: true
       ** - hasCompletePet: true
       ** - showCustomerInfoStep: true
       */
      transition(
        ACTION_TYPES.toUserInfo,
        STATE.userInfo,
        guard(hasNoCustomer),
        guard(hasCompletePet),
        guard(showCustomerInfoStep),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.userInfo,
          nextKey: STATE.recommendedPlan,
          pet: ctx.pet,
          extras: payload.extras,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Recommended Plan Page if
       ** - allPetsComplete: true
       ** - hasCustomer: (dependent on skipCustomerInfo flag)
       */
      transition(
        ACTION_TYPES.toRecommendedPlan,
        STATE.recommendedPlan,
        guard(allPetsComplete),
        guard(hasCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.recommendedPlan,
          nextKey: STATE.extras,
          planType: payload.planType,
          recipes: { ...ctx.recipes },
          flags: { ...ctx.flags, ...payload.flags },
          pet: payload.pet,
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout Page if
       ** - allPetsComplete: true
       ** - allPetsHaveProductInCart: true
       ** - hasCustomer: (dependent on skipCustomerInfo flag)
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        guard(allPetsComplete),
        guard(allPetsHaveProductInCart),
        guard(hasCustomer),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
          flags: { ...ctx.flags, ...payload.flags },
          planType: payload.planType,
          pet: payload.pet,
        })),
      ),
    ),
    [STATE.userInfo]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions Back to Allergies Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.allergies,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.allergies,
          nextKey: STATE.userInfo,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to recommended Plan Page if
       ** - hasCustomer: true
       ** - allPetsComplete: true
       */
      transition(
        ACTION_TYPES.toRecommendedPlan,
        STATE.recommendedPlan,
        guard(hasCustomer),
        guard(allPetsComplete),
        reduce(({ ...ctx }, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.recommendedPlan,
          nextKey: STATE.extras,
          planType: payload.planType,
          recipes: { ...ctx.recipes },
          allergies: payload.allergies,
          pet: payload.pet,
          customer: payload.customer,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.recommendedPlan]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Allergies Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.allergies,
        guard(skipUserInfoOnBack),
        reduce(
          (ctx, { payload }) => ({
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.allergies,
            nextKey: STATE.recommendedPlan,
            planType: payload.planType,
            customer: { ...ctx.customer },
            recipes: { ...ctx.recipes },
            pet: payload.pet,
            flags: { ...ctx.flags, ...payload.flags },
          }),
          reduce(goBackReducer),
        ),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to User Info Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.userInfo,
        guard(showCustomerInfoStep),
        reduce(
          (ctx, { payload }) => ({
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.userInfo,
            nextKey: STATE.recommendedPlan,
            planType: payload.planType,
            customer: { ...ctx.customer },
            recipes: { ...ctx.recipes },
            flags: { ...ctx?.flags, ...payload?.flags },
            pet: payload.pet,
          }),
          reduce(goBackReducer),
        ),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to submit cart function
       */
      transition(
        ACTION_TYPES.selectedFreshRecipes,
        STATE.submitCart,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.extras,
            planType: payload.planType,
            recipes: { ...ctx.recipes, fresh: payload.recipes },
            flags: { ...ctx.flags, ...payload.flags },
          };
        }),
      ),
      transition(
        ACTION_TYPES.selectedMixedRecipes,
        STATE.submitCart,
        reduce((ctx, { payload }) => {
          return {
            ...ctx,
            prevState: { ...ctx },
            stateKey: STATE.recommendedPlan,
            nextKey: STATE.extras,
            planType: payload.planType,
            recipes: { dry: payload.recipes.dry, fresh: payload.recipes.fresh },
            flags: { ...ctx.flags, ...payload.flags },
          };
        }),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Extras Page if
       ** - hasCompletePet: true
       */
      transition(
        ACTION_TYPES.toExtras,
        STATE.extras,
        guard(hasCompletePet),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.recommendedPlan,
          nextKey: STATE.extras,
          planType: payload.planType,
          recipes: { ...ctx.recipes },
          pet: payload.pet,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout if
       ** - allPetsComplete: true
       ** - hasCustomer: (dependent on skipCustomerInfo flag)
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        guard(hasCustomer),
        guard(allPetsComplete),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
          planType: payload.planType,
          recipes: { ...ctx.recipes },
          pet: payload.pet,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.submitCart]: invoke(
      saveCart,
      /**
       ** Transitions to recommended Plan Page if
       ** - submitCartFailed: true
       */
      transition(
        'done',
        STATE.recommendedPlan,
        guard(submitCartFailed),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.recommendedPlan,
          nextKey: STATE.extras,
        })),
      ),

      /**
       ** Transitions to Extras Page
       */
      transition(
        'done',
        STATE.extras,
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.extras,
          nextKey: STATE.checkout,
        })),
      ),
    ),
    [STATE.extras]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Recommended Plan Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.recommendedPlan,
        reduce(ctx => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.recommendedPlan,
          nextKey: STATE.extras,
          pet: ctx.pet,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout if
       ** - hasCustomer: true
       ** - allPetsComplete: true
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        guard(hasCustomer),
        guard(allPetsComplete),
        reduce((ctx, { payload }) => ({
          ...ctx,
          prevState: { ...ctx },
          stateKey: STATE.checkout,
          extras: payload.extras,
          pet: payload.pet,
          pets: payload.pets,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.deletePet]: invoke(
      async ctx => {
        return ctx.deletePetFromCart(ctx.pet_id || ctx.pet.id);
      },
      transition(
        'done',
        STATE.checkout,
        reduce((ctx, { data }) => ({
          ...ctx,
          stateKey: STATE.checkout,
          cart: data.cart,
          pets: data.pets,
          pet: data.pets[0],
          pet_id: data.pets[0].id,
          flags: { ...ctx.flags },
        })),
      ),
      transition(
        'error',
        STATE.checkout,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.checkout,
        })),
      ),
    ),
    [STATE.checkout]: final(),
  },
  createInitialContext,
);
